import { initializeApp } from "firebase/app";
import { getAuth, onAuthStateChanged, GoogleAuthProvider, setPersistence, browserLocalPersistence } from "firebase/auth";
import {
  initializeFirestore,
  persistentLocalCache,
  persistentMultipleTabManager,
  collection,
} from "firebase/firestore";
import { getAnalytics, logEvent as firebaseLogEvent, Analytics } from "firebase/analytics";
import { getMessaging, isSupported, Messaging } from "firebase/messaging";
import { getFunctions, httpsCallable } from "firebase/functions";
import { Capacitor } from "@capacitor/core";
export { getToken, onMessage } from "firebase/messaging";
export { signOut, signInWithCustomToken, signInWithPopup } from "firebase/auth";

// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
setPersistence(auth, browserLocalPersistence).catch(() => {});
const googleProvider = new GoogleAuthProvider();
const db = initializeFirestore(app, {
  localCache: persistentLocalCache({
    tabManager: persistentMultipleTabManager(),
  }),
});
const functions = getFunctions(app);

// Firebase Analytics — initialise on web only (native uses the native SDK)
let analytics: Analytics | null = null;
const IS_DEV = process.env.NODE_ENV === "development";
if (!Capacitor.isNativePlatform()) {
  try { analytics = getAnalytics(app); } catch { /* analytics unavailable */ }
}

/** Log an analytics event (no-op if analytics is unavailable) */
export function logAnalyticsEvent(
  eventName: string,
  params?: Record<string, string | number | boolean>,
) {
  if (!analytics) return;
  // In dev mode, attach debug_mode so events show in Firebase DebugView
  const eventParams = IS_DEV ? { ...params, debug_mode: true } : params;
  firebaseLogEvent(analytics, eventName, eventParams);
}


onAuthStateChanged(auth, () => {});

// Firebase Cloud Messaging — only on web browsers that support it
let messaging: Messaging | null = null;

if (!Capacitor.isNativePlatform()) {
  isSupported().then((supported) => {
    if (supported) {
      messaging = getMessaging(app);
    }
  });
}

export const getMessagingInstance = () => messaging;

// collections on the DB, how to get from the db
const dbUsers = collection(db, "users");
const dbNotes = collection(db, "notes");
const dbLists = collection(db, "lists");
const dbListItems = collection(db, "listItems");
const dbTasks = collection(db, "tasks");
const dbTaskTemplates = collection(db, "taskTemplates");
const dbFeatureFlags = collection(db, "featureFlags");
const dbPayments = collection(db, "payments");
const dbDailyLogs = collection(db, "dailyLogs");
const dbFeatureRequests = collection(db, "featureRequests");
const dbAnnouncements = collection(db, "announcements");
const dbLiftLogs = collection(db, "liftLogs");
const dbCommunityPosts = collection(db, "communityPosts");
const dbCommunityReactions = collection(db, "communityReactions");
const dbCommunityReports = collection(db, "communityReports");
const dbCardioLogs = collection(db, "cardioLogs");
const dbWorkoutSessions = collection(db, "workoutSessions");
const dbActivityLogs = collection(db, "activityLogs");
const dbCycleDayLogs = collection(db, "cycleDayLogs");
const dbCustomWorkoutPlans = collection(db, "customWorkoutPlans");
const dbBodyWeightLogs = collection(db, "bodyWeightLogs");
const dbReferrals = collection(db, "referrals");
const dbPayoutRequests = collection(db, "payoutRequests");
const dbSharedTasks = collection(db, "sharedTasks");
const dbNotifications = collection(db, "notifications");
const dbVirtualPets = collection(db, "virtualPets");
const dbBooks = collection(db, "books");
const dbBookCatalog = collection(db, "bookCatalog");
const dbProteinLogs = collection(db, "proteinLogs");
const dbFoodLogs = collection(db, "foodLogs");
const dbHabits = collection(db, "habits");
const dbRunningShoes = collection(db, "runningShoes");
const dbCommunityPlans = collection(db, "communityPlans");
const dbCoachProfiles = collection(db, "coachProfiles");
const dbCommunityFollows = collection(db, "communityFollows");
const dbExpenses = collection(db, "expenses");
const dbCommunityBookmarks = collection(db, "communityBookmarks");
const dbCommunityPollVotes = collection(db, "communityPollVotes");
const dbCommunityGroups = collection(db, "communityGroups");
const dbCommunityGroupMembers = collection(db, "communityGroupMembers");
const dbMovies = collection(db, "movies");
const dbMovieCatalog = collection(db, "movieCatalog");
const dbMusic = collection(db, "music");
const dbMusicCatalog = collection(db, "musicCatalog");
const dbRecipes = collection(db, "recipes");
const dbBetaRequests = collection(db, "betaRequests");
const dbSupportTickets = collection(db, "supportTickets");
const dbStepLogs = collection(db, "stepLogs");

export { auth, db, functions, googleProvider, httpsCallable, onAuthStateChanged, dbUsers, dbNotes, dbLists, dbListItems, dbTasks, dbTaskTemplates, dbFeatureFlags, dbPayments, dbDailyLogs, dbFeatureRequests, dbAnnouncements, dbLiftLogs, dbCommunityPosts, dbCommunityReactions, dbCommunityReports, dbCardioLogs, dbWorkoutSessions, dbActivityLogs, dbCycleDayLogs, dbCustomWorkoutPlans, dbBodyWeightLogs, dbReferrals, dbPayoutRequests, dbSharedTasks, dbNotifications, dbVirtualPets, dbBooks, dbBookCatalog, dbProteinLogs, dbFoodLogs, dbHabits, dbRunningShoes, dbCommunityPlans, dbCoachProfiles, dbCommunityFollows, dbExpenses, dbCommunityBookmarks, dbCommunityPollVotes, dbCommunityGroups, dbCommunityGroupMembers, dbMovies, dbMovieCatalog, dbMusic, dbMusicCatalog, dbRecipes, dbBetaRequests, dbSupportTickets, dbStepLogs };
